import React from 'react';
import { applicantStore } from '../ApplicantStore';
import { CandidateProfileTopSkills } from 'components/candidates/profile/top-skills/CandidateProfileTopSkills';
import { CandidateScorecardsView } from 'candidate-public-profile/components/sections/scorecards-view/CandidateScorecardsView';
import { CandidateProfileCertifications } from 'candidate-public-profile/components/sections/CandidateProfileCertifications';
import { CandidateProfileEducation } from 'candidate-public-profile/components/sections/CandidateProfileEducation';
import { CandidateProfileExperience } from 'candidate-public-profile/components/sections/CandidateProfileExperience';
import { CandidatePublicProfileTestResults } from 'candidate-public-profile/components/sections/CandidatePublicProfileTestResults';

function stripDemoMarker(text) {
	return text?.replace(/\[rich-profile-demo\]\s*/i, '').trim() || '';
}

export function hasPublicProfileContent(candidate) {
	if (!candidate) return false;

	const bio = stripDemoMarker(candidate.aboutMe);
	const {
		candidateExperiences = [],
		candidateEducations = [],
		candidateCertifications = [],
		candidateTestResults = [],
		candidateScorecards = [],
		candidateTags = [],
		topSkills = []
	} = candidate;

	return Boolean(
		bio ||
		topSkills.length ||
		candidateTags.length ||
		candidateExperiences.length ||
		candidateScorecards.length ||
		candidateTestResults.length ||
		candidateEducations.length ||
		candidateCertifications.length
	);
}

function ProfileSection({ title, children, className = '' }) {
	if (!children) return null;

	return (
		<section className={`profile-redesign__section ${className}`.trim()}>
			<h4 className="profile-redesign__section-title">{title}</h4>
			<div className="profile-redesign__section-body">{children}</div>
		</section>
	);
}

function ApplicantProfileTags({ tags }) {
	if (!tags?.length) return null;

	return (
		<div className="profile-redesign__tags">
			{tags.map(item => {
				const tag = item.tag || item;
				const isBlacklisted = tag.name === 'Blacklisted';
				const variant = isBlacklisted
					? 'profile-redesign__tag--muted'
					: 'profile-redesign__tag--accent';
				const iconKey =
					applicantStore.featuredTagsIcons[tag.name?.toLowerCase()];
				const iconSrc = iconKey ? `/ats/icons/${iconKey}.svg` : null;

				return (
					<span key={tag.id} className={`profile-redesign__tag ${variant}`}>
						{iconSrc ? (
							<img
								src={iconSrc}
								alt=""
								className="profile-redesign__tag-icon"
								aria-hidden
							/>
						) : null}
						{tag.name}
					</span>
				);
			})}
		</div>
	);
}

export default function ApplicantPublicProfileContent({ candidate }) {
	const bio = stripDemoMarker(candidate.aboutMe);
	const {
		candidateExperiences = [],
		candidateEducations = [],
		candidateCertifications = [],
		candidateTestResults = [],
		candidateScorecards = [],
		candidateTags = [],
		topSkills = []
	} = candidate;

	const hasBio = Boolean(bio);
	const hasTopSkills = topSkills.length > 0;
	const hasTags = candidateTags.length > 0;
	const hasExperience = candidateExperiences.length > 0;
	const hasScorecards = candidateScorecards.length > 0;
	const hasTests = candidateTestResults.length > 0;
	const hasEducation = candidateEducations.length > 0;
	const hasCertifications = candidateCertifications.length > 0;

	if (!hasPublicProfileContent(candidate)) {
		return null;
	}

	return (
		<div className="profile-redesign">
			{hasBio ? (
				<ProfileSection title="Bio:" className="profile-redesign__section--bio">
					<p className="profile-redesign__bio-text">{bio}</p>
					<button type="button" className="profile-redesign__ai-button">
						<img
							src="/ats/icons/improve-with-ai-icon.svg"
							alt=""
							className="profile-redesign__ai-button-icon"
							aria-hidden
						/>
						<span>Improve with AI</span>
					</button>
				</ProfileSection>
			) : null}

			{hasTopSkills ? (
				<ProfileSection
					title="Top Technologies:"
					className="profile-redesign__section--top-tech">
					<div className="profile-redesign__skill-tags">
						<CandidateProfileTopSkills skills={topSkills} />
					</div>
				</ProfileSection>
			) : null}

			{hasTags ? (
				<ProfileSection
					title="Tags"
					className="profile-redesign__section--tags">
					<ApplicantProfileTags tags={candidateTags} />
				</ProfileSection>
			) : null}

			{hasExperience ? (
				<ProfileSection
					title="Experience"
					className="profile-redesign__section--experience">
					<div className="profile-redesign__experience-list">
						<CandidateProfileExperience experiences={candidateExperiences} />
					</div>
				</ProfileSection>
			) : null}

			{hasScorecards ? (
				<ProfileSection
					title="Scorecards"
					className="profile-redesign__section--scorecards">
					<CandidateScorecardsView
						candidate={candidate}
						scorecards={candidateScorecards}
						circleSize={14}
						fullCircleColor="#314EBD"
						emptyCircleColor="#D4E1FF"
						showInternalNotes
					/>
				</ProfileSection>
			) : null}

			{hasTests ? (
				<ProfileSection
					title="Technical Tests"
					className="profile-redesign__section--tests">
					<CandidatePublicProfileTestResults
						candidate={candidate}
						testResults={candidateTestResults}
						chartMargin={{ top: 50, right: 24, bottom: 72, left: 56 }}
						chartHeight={250}
					/>
				</ProfileSection>
			) : null}

			{hasEducation ? (
				<ProfileSection
					title="Education"
					className="profile-redesign__section--education">
					<div className="profile-redesign__cards">
						<CandidateProfileEducation educations={candidateEducations} />
					</div>
				</ProfileSection>
			) : null}

			{hasCertifications ? (
				<ProfileSection
					title="Certifications"
					className="profile-redesign__section--certifications">
					<div className="profile-redesign__cards">
						<CandidateProfileCertifications
							certifications={candidateCertifications}
						/>
					</div>
				</ProfileSection>
			) : null}
		</div>
	);
}
